import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import type { AltchaChallenge, AltchaSolution } from '@shared/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi, AuthApiError } from '../../api/authApi';
import { ME_QUERY_KEY } from '../../queries/useMe';
import { useAuthStore } from '../../store/useAuthStore';
import { AltchaWidget } from './AltchaWidget';

const BLOCKING_CODES = new Set(['too_many_attempts', 'ip_rate_limited']);

export function LogInTab(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const closeModal = useAuthStore((s) => s.closeAuthModal);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [altcha, setAltcha] = useState<AltchaSolution | null>(null);
  const [challenge, setChallenge] = useState<AltchaChallenge | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  // Tick once per second so the countdown updates and the block lifts automatically.
  useEffect(() => {
    if (blockedUntil === null) return;
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [blockedUntil]);

  const isBlocked = blockedUntil !== null && now < blockedUntil;
  const secondsRemaining = isBlocked && blockedUntil !== null ? blockedUntil - now : 0;
  const captchaInProgress = challenge !== null && altcha === null;

  // Clear stale block once expired.
  useEffect(() => {
    if (blockedUntil !== null && now >= blockedUntil) {
      setBlockedUntil(null);
      setError(null);
    }
  }, [now, blockedUntil]);

  const onSolved = useCallback((s: AltchaSolution) => setAltcha(s), []);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (isBlocked || captchaInProgress) return;
    setLoading(true);
    setError(null);
    try {
      const payload = altcha ? { email, password, altcha } : { email, password };
      const data = await authApi.login(payload);
      setUser(
        {
          userId: data.user_id,
          nickname: null,
          email: null,
          authMethod: 'email',
          tierId: 2,
          tierKey: 'regular',
          ageVerificationStatus: 'pending',
        },
        data.csrf_token,
      );
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      closeModal();
      navigate('/match');
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === 'altcha_required' && err.details?.['altcha_challenge']) {
          setChallenge(err.details['altcha_challenge'] as AltchaChallenge);
          setAltcha(null);
          setError('Please complete the verification below.');
        } else if (err.status === 429 && BLOCKING_CODES.has(err.code)) {
          const retryAfter = Number(err.details?.['retry_after'] ?? 60);
          setBlockedUntil(Math.floor(Date.now() / 1000) + retryAfter);
          setNow(Math.floor(Date.now() / 1000));
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = (() => {
    if (loading) return 'Signing in…';
    if (isBlocked) return `Try again in ${secondsRemaining}s`;
    if (captchaInProgress) return 'Solve verification…';
    return 'Log In';
  })();

  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoFocus
        fullWidth
        disabled={isBlocked}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        disabled={isBlocked}
      />
      {challenge && <AltchaWidget challenge={challenge} onSolved={onSolved} />}
      {error && <Alert severity={isBlocked ? 'warning' : 'error'}>{error}</Alert>}
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={loading || isBlocked || captchaInProgress}
        fullWidth
      >
        {buttonLabel}
      </Button>
    </Box>
  );
}
